import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { EASE, MaskedLines } from '../motion';
import { ChromeStar } from '../ChromeStar';
import { SmartImg } from '../Image';

function Corner({ className, children, align = 'left', star = false }) {
  return (
    <div
      className={`pointer-events-none absolute z-20 ${
        align === 'right' ? 'text-right' : ''
      } ${className}`}
    >
      {star && (
        <ChromeStar
          className={`mb-4 h-3.5 w-3.5 opacity-80 ${align === 'right' ? 'ml-auto' : ''}`}
        />
      )}
      <div className="font-mono text-[8px] uppercase leading-[2] tracking-[0.24em] text-neutral-500 sm:text-[9px]">
        {children}
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const yMark = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yRibbons = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yPhones = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yStar = useTransform(scrollYProgress, [0, 1], [0, 170]);

  const scrollToShop = () => {
    const el = document.getElementById('shop');
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { duration: 1.5, offset: -110 });
    } else {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col overflow-hidden bg-white"
    >
      <svg aria-hidden="true" className="absolute h-0 w-0">
        <defs>
          <filter id="liquid-chrome">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.012 0.018"
              numOctaves="2"
              seed="7"
              result="noise"
            >
              <animate
                attributeName="baseFrequency"
                dur="16s"
                values="0.012 0.018;0.017 0.024;0.012 0.018"
                repeatCount="indefinite"
              />
            </feTurbulence>
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="9"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      {/* liquid chrome ribbons — desktop: full-bleed sweep; mobile: two
          corner pieces with feathered edges, bottom wave anchored low so it
          never touches the tagline band */}
      <motion.div
        style={{ y: yRibbons }}
        className="pointer-events-none absolute inset-0 z-0"
      >
        <SmartImg
          src="/assets/chrome-ribbons.png"
          alt=""
          label=""
          eager
          className="hidden h-full w-full object-cover sm:block sm:scale-[1.18]"
        />
        <SmartImg
          src="/assets/ribbons-tl-mobile.png"
          alt=""
          label=""
          eager
          className="absolute left-0 top-0 w-[64vw] sm:hidden"
        />
        <SmartImg
          src="/assets/ribbons-br-mobile.png"
          alt=""
          label=""
          eager
          className="absolute -bottom-[10%] -right-[18%] w-[72vw] sm:hidden"
        />
      </motion.div>

      <Corner className="left-10 top-28 hidden sm:block" star>
        Milano, IT
        <br />
        Est. 2026
        <br />
        Objects for
        <br />
        bad decisions.
      </Corner>
      <Corner className="right-4 top-28 hidden sm:right-10 sm:block" align="right" star>
        Same
        <br />
        world.
        <br />
        Different
        <br />
        taste.
      </Corner>
      <Corner className="bottom-6 right-4 hidden sm:bottom-8 sm:right-10 sm:block" align="right">
        <span className="text-neutral-800">01 / Chrome</span>
        <span className="mx-0 my-2 block h-px w-10 bg-black/30 sm:ml-auto" />
        A new classic.
        <br />
        Wired.
        <br />
        Forever relevant.
      </Corner>

      <motion.div
        style={{ y: yStar }}
        className="pointer-events-none absolute right-[10%] top-[24%] z-10 sm:right-[14%]"
      >
        <motion.div
          animate={{ scale: [1, 1.14, 1] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChromeStar className="h-8 w-8 sm:h-12 sm:w-12" spin />
        </motion.div>
      </motion.div>

      <div className="relative z-10 flex flex-1 translate-y-[4vh] flex-col items-center justify-center px-6 pb-16 pt-28">
        <motion.div
          style={{ y: yMark }}
          className="relative w-[92vw] sm:w-[min(44vw,72vh)]"
        >
          <motion.div
            initial={{ clipPath: 'inset(0% 0% 100% 0%)', scale: 1.07, opacity: 0.35 }}
            animate={{ clipPath: 'inset(0% 0% 0% 0%)', scale: 1, opacity: 1 }}
            transition={{ duration: 1.5, ease: EASE, delay: 0.3 }}
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div style={{ filter: 'url(#liquid-chrome)' }}>
                <SmartImg
                  src="/assets/wordmark-chrome.png"
                  alt="VIZIO"
                  label="VIZIO"
                  eager
                  className="w-full"
                />
              </div>
            </motion.div>
          </motion.div>
          <span className="absolute -right-2 top-0 font-mono text-[10px] text-neutral-600 sm:-right-4 sm:text-xs">
            ®
          </span>
        </motion.div>

        <MaskedLines
          className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.45em] text-neutral-800 sm:mt-6 sm:text-xs"
          delay={1.15}
          lines={["Things you don't need."]}
        />
        <MaskedLines
          className="mt-2 text-center font-mono text-[8px] uppercase tracking-[0.4em] text-neutral-500 sm:text-[10px]"
          delay={1.35}
          lines={["But you'll want them anyway."]}
        />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.75, duration: 0.8, ease: EASE }}
          className="mt-5 sm:mt-6"
        >
          <button
            type="button"
            data-testid="hero-shop-cta"
            onClick={scrollToShop}
            className="rounded-full bg-ink px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.32em] text-bone transition-colors duration-300 hover:bg-neutral-700"
          >
            Discover 01 →
          </button>
        </motion.div>
      </div>

      {/* product render — giant, BEHIND the wordmark; the band mask keeps
          the taglines + CTA permanently clear (mobile) */}
      <div className="band-mask pointer-events-none absolute inset-0 z-[5]">
        <motion.div
          style={{ y: yPhones }}
          className="absolute bottom-[-12%] left-[-24%] sm:bottom-[-12%] sm:left-[22%]"
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 1.2, ease: EASE }}
          >
            <SmartImg
              src="/assets/earphones-hero.png"
              alt="01 / CHROME — auricolari cablati in cromo"
              label="01 / Chrome"
              eager
              className="h-[88vh] w-auto max-w-none sm:h-[105vh]"
            />
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ArrowDown className="h-4 w-4 text-neutral-600" strokeWidth={1} />
        </motion.div>
      </motion.div>
    </section>
  );
}
